import type { Content, PageBreak, StyleDictionary } from 'pdfmake/interfaces';
import { MAGIC_SCALING_NUMBER, pdfTheme } from '../pdfTheme';
import type { BaseReportComponent } from './BaseReportComponent';
import { ImageComponent, type ImageReference } from './ImageComponent';
import type { TitleBar } from './TitleBar';

interface ImageWithTitleOptions {
	width: number;
	height: number;
	sectionTitle?: TitleBar;
	pageBreak?: PageBreak;
}

export interface PublicImageWithTitleOptions {
	width?: number;
	height?: number;
	sectionTitle?: TitleBar;
	pageBreak?: ImageWithTitleOptions['pageBreak'] | 'none';
}
const fontSize = 14;
export class ImageWithTitle implements BaseReportComponent {
	nodes: BaseReportComponent[] = [];
	private options: ImageWithTitleOptions;
	private imageComponent: ImageComponent;

	constructor(
		private imageTitle: string,
		private imageReference: ImageReference,
		opts: Partial<PublicImageWithTitleOptions> = {}
	) {
		const defaultOptions: ImageWithTitleOptions = { width: pdfTheme.page.width, height: pdfTheme.page.height };

		//TODO: clean up pagebreaking logic. we do this here because elsewhere the titlebar is responsible for pagebreaking
		// and we only have a titlebar sometimes
		const pageBreak =
			opts.pageBreak === 'none' || opts.sectionTitle ? undefined : (opts.pageBreak ?? defaultOptions.pageBreak);
		this.options = {
			width: opts.width ?? defaultOptions.width,
			sectionTitle: opts.sectionTitle,
			height: opts.height ?? defaultOptions.height,
			pageBreak,
		};
		if (this.options.sectionTitle) {
			this.nodes.push(this.options.sectionTitle);
		}
		const headersHeight =
			(this.options.sectionTitle ? this.options.sectionTitle.height : 0) + fontSize * MAGIC_SCALING_NUMBER;
		this.imageComponent = new ImageComponent(
			this.imageReference,
			this.options.width,
			this.options.height - headersHeight
		);
		this.nodes.push(this.imageComponent);
	}

	exportContent(): Content {
		return {
			unbreakable: true,
			margin: [0, 0],
			alignment: 'center',
			pageBreak: this.options.pageBreak,
			stack: [
				...(this.options.sectionTitle ? [this.options.sectionTitle.exportContent()] : []),
				{
					text: this.imageTitle,
					style: ['imageTitle'],
				},
				this.imageComponent.exportContent(),
			],
		};
	}

	exportStyles(): StyleDictionary {
		return {
			imageTitle: {
				fontSize,
				alignment: 'center',
				color: pdfTheme.font.color.dark,
			},
		};
	}
}
